<template>
  <section id="your-ingredients" class="your-ingredients ws-grid">
    <section-title
      :title="$t('section.yourIngredients.title.part1')"
      class="your-ingredients__title"
    />

    <div class="your-ingredients__content your-ingredients-content">
      <your-ingredients-chart
        v-show="currentSlideIndex === 0"
        ref="ingredientsChart"
        class="your-ingredients-content__chart"
        :ingredients="ingredients"
        @ingredient-click="onIngredientClick"
      />
      <template v-if="shouldLoadVideos">
        <responsive-video
          v-for="name of ingredientNames"
          v-show="currentIngredientName === name"
          :key="name"
          class="your-ingredients-content__video"
          loop
          muted
          playsinline="playsinline"
          :video-resource-name="name"
          :is-playing="currentIngredientName === name"
        />
      </template>

      <swiper-type-four
        ref="swiper"
        class="your-ingredients-content__swiper swiper"
        :items="slides"
        @change="onSlideChange"
      >
        <template v-slot:item="{ item }">
          <ingredients-summary v-if="!item.name" class="swiper__slide" :ingredients="ingredients" />
          <ingredient-details
            v-else
            class="swiper__slide"
            :ingredient="item"
            :is-visible="currentSlideIndex === item.index"
          />
        </template>
      </swiper-type-four>
    </div>
  </section>
</template>

<script>
import { mapGetters } from 'vuex';

import IngredientDetails from '@/components/your-ingredients/IngredientDetails.vue';
import IngredientsSummary from '@/components/your-ingredients/IngredientsSummary.vue';
import SwiperTypeFour from '@/components/common/app-swiper/SwiperTypeFour.vue';
import SectionTitle from '@/components/common/SectionTitle.vue';
import YourIngredientsChart from '@/components/your-ingredients/YourIngredientsChart.vue';
import ResponsiveVideo from '@/components/common/ResponsiveVideo.vue';

import { getProductOptionsDefaultOption } from '@/models/core/ProductOptions';
import { hasProductPackIngredients, getProductPackAllIngredients } from '@/models/core/ProductPack';
import { getIngredientName, sortIngredients } from '@/models/core/Ingredient';

import { types } from '@/store/types';
import { PRODUCT_NAMES } from '@/constants/treatment';
import { OBSERVER_THRESHOLDS } from '@/constants/common';

const setUpIntersectionObserverForElement = (handler, element, options = { threshold: 1 }) => {
  const intersectionObserver = new IntersectionObserver((entries, observer) => {
    entries.forEach(entry => handler(entry, observer));
  }, options);

  intersectionObserver.observe(element);
  return intersectionObserver;
};

export default {
  name: 'YourIngredients',
  components: {
    IngredientDetails,
    IngredientsSummary,
    SwiperTypeFour,
    SectionTitle,
    YourIngredientsChart,
    ResponsiveVideo
  },
  data() {
    return {
      currentSlideIndex: 0,
      shouldLoadVideos: false
    };
  },
  computed: {
    ...mapGetters({
      allProductOptions: types.getters.ALL_PRODUCT_OPTIONS_SETS
    }),
    ingredients() {
      const faceSerumOptions = this.allProductOptions[PRODUCT_NAMES.FACE_SERUM];
      const eyeSerumOptions = this.allProductOptions[PRODUCT_NAMES.EYE_CONTOUR_SERUM];
      const faceSerum = getProductOptionsDefaultOption(faceSerumOptions);
      const eyeSerum = getProductOptionsDefaultOption(eyeSerumOptions);

      const ingredients = hasProductPackIngredients(faceSerum)
        ? getProductPackAllIngredients(faceSerum)
        : getProductPackAllIngredients(eyeSerum);

      return sortIngredients(ingredients);
    },
    ingredientNames() {
      return this.ingredients.map(getIngredientName);
    },
    slides() {
      return [{}, ...this.ingredients].map((item, index) => ({ ...item, index }));
    },
    currentIngredientName() {
      const ingredient = this.slides[this.currentSlideIndex];

      return getIngredientName(ingredient);
    }
  },
  mounted() {
    this.setUpVideoIntersectionObserver();
  },
  methods: {
    onSlideChange(index) {
      this.currentSlideIndex = index;
    },
    navigateToPage(page) {
      this.$refs.swiper.navigateToPage(page);
    },
    onIngredientClick(ingredientName) {
      const page = this.slides.findIndex(({ name }) => ingredientName === name);

      this.navigateToPage(page);
    },
    setUpVideoIntersectionObserver() {
      const observerElement = this.$refs.ingredientsChart.$el;
      const observerOptions = { threshold: OBSERVER_THRESHOLDS.ZERO };

      const observerHandler = (entry, observer) => {
        if (entry.isIntersecting) {
          this.shouldLoadVideos = true;

          observer.disconnect();
        }
      };

      setUpIntersectionObserverForElement(observerHandler, observerElement, observerOptions);
    }
  }
};
</script>

<style lang="scss" scoped>
.your-ingredients {
  color: var(--u-color-grey-900);
  padding: 40px var(--ws-grid-m, 16px);

  &__title {
    margin-bottom: 32px;
  }
}

.your-ingredients-content {
  &__chart {
    width: 100%;
    margin-bottom: 32px;
    margin-inline-start: auto;
    margin-inline-end: auto;
  }

  &__video {
    margin-bottom: 32px;
    width: 260px;
    max-width: 100%;
    margin-left: auto;
    margin-right: auto;
    display: block;
  }
}

@media (min-width: 600px) {
  .your-ingredients-content {
    &__chart {
      width: var(--ws-col-4);
    }

    &__video {
      max-width: 450px;
    }

    &__swiper {
      width: calc(var(--ws-col-6) + var(--ws-grid-g) * 2);
      margin-inline-start: auto;
      margin-inline-end: auto;
      max-width: 450px;
    }
  }
}

@media (min-width: 960px) {
  .your-ingredients {
    &__title {
      margin-bottom: 48px;
    }
  }

  .your-ingredients-content {
    display: flex;

    &__chart,
    &__video {
      margin-bottom: 0;
      width: var(--ws-col-4);
    }

    &__video {
      margin-inline-start: calc(var(--ws-col-1) + var(--ws-grid-g));
    }

    &__swiper {
      width: var(--ws-col-5);
      margin-inline-start: auto;
      margin-inline-end: 0;
      max-width: unset;
    }
  }

  .swiper {
    &__slide {
      max-width: 450px;
    }
  }
}

@media (min-width: 1440px) {
  .your-ingredients-content {
    &__chart,
    &__video {
      width: var(--ws-col-5);
    }
  }
}

@media (min-width: 1920px) {
  .your-ingredients {
    &__title {
      margin-bottom: 64px;
    }
  }
}
</style>
